








import { Component, Prop, Vue } from 'vue-property-decorator'
import { ChartData, ChartOptions } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { User } from '@/api/model/user'
import { Branch } from '@/api/model/branch'

export interface PerSubjectPieChartData {
    [subjectId: string]: {
        subject: User|Branch,
        value: number,
    }
}

@Component
export default class PerSubjectPieChart extends Vue {
    @Prop({ required: true, default: () => [] }) readonly data!: PerSubjectPieChartData
    @Prop() readonly formatter!: (value: number, precision?: number) => string

    get subjectIds(): string [] {
        return Object.keys(this.data)
    }

    get sortedSubjectIds(): string[] {
        return this.subjectIds
            .sort((a, b) => {
                return this.data[b].value - this.data[a].value
            })
            .filter(subjectId => this.data[subjectId].value > 0)
    }

    get chartData(): ChartData {
        const labels = []
        const data = []
        let backgroundColor = []

        this.sortedSubjectIds.forEach(subjectId => {
            labels.push(this.data[subjectId].subject.name)
            data.push(this.data[subjectId].value)
        })

        if (this.isEmpty) {
            data.push(1)
            backgroundColor = ['rgb(232, 234, 238)']
        } else {
            backgroundColor = [
                // chartjs-plugin-colorschemes: tableau.Classic20
                '#1f77b4',
                '#aec7e8',
                '#ff7f0e',
                '#ffbb78',
                '#2ca02c',
                '#98df8a',
                '#d62728',
                '#ff9896',
                '#9467bd',
                '#c5b0d5',
                '#8c564b',
                '#c49c94',
                '#e377c2',
                '#f7b6d2',
                '#7f7f7f',
                '#c7c7c7',
                '#bcbd22',
                '#dbdb8d',
                '#17becf',
                '#9edae5',
            ]
        }

        return {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor,
                    datalabels: {
                        color: 'rgba(255, 255, 255, .75)',
                    },
                },
            ],
        }
    }

    get chartOptions(): ChartOptions<'pie'> {
        return {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2,

            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: !this.isEmpty,
                    callbacks: {
                        label: tooltipItem => {
                            let label = tooltipItem.label + ': ' + this.formatValue(tooltipItem.raw as number)

                            if (tooltipItem.raw > 0) {
                                label += ' (' + this.formatPercentage(tooltipItem.raw as number / this.totals.value) + ')'
                            }

                            return label
                        },
                    },
                },
                datalabels: {
                    formatter: (value, context) => {
                        if (!this.totals.value) {
                            return ''
                        }

                        let datalabel = ''

                        const percentage = value / this.totals.value
                        if (percentage > .10) {
                            datalabel = this.formatPercentage(percentage) + '\n' + datalabel
                        }
                        if (percentage > 0.20 && context.chart.data.labels[context.dataIndex]) {
                            datalabel = context.chart.data.labels[context.dataIndex] + '\n' + datalabel
                        }

                        return datalabel.trim()
                    },
                },
            },
        }
    }

    get chartPlugins(): object[] {
        return [
            ChartDataLabels,
        ]
    }

    get totals() {
        const total = {
            value: 0,
        }

        this.sortedSubjectIds.forEach(subjectId => {
            total.value += this.data[subjectId].value
        })

        return total
    }

    get isEmpty(): boolean {
        return this.sortedSubjectIds.length === 0
    }

    formatSubject(subject: User|Branch): string {
        return subject.name
    }

    formatValue(value: number, precision?: number): string {
        value = Number(value)

        return this.formatter ? this.formatter(value, precision) : value.toString()
    }

    formatPercentage(percentage: number): string {
        if (percentage === null) {
            return ''
        }

        return (percentage || 0).toLocaleString('nl', { style: 'percent' })
    }
}
